import { FILTER_OPTIONS_DIVIDER } from '@repo/common/components/Filter/constants';
import type { FilterOption } from '@repo/common/components/Filter/types';
import { FilterOptionType } from '@repo/common/components/Filter/types';

/**
 * Inserts a semantic view type filter after the TITLE filter in the filter options array.
 * Also removes the native type filter since semantic view types replace it.
 * If TITLE filter is not found, inserts at the beginning.
 * If condition is false, returns the base filter options unchanged.
 *
 * @param baseFilterOptions - The base filter options array
 * @param filterToInsert - The filter option to insert
 * @param condition - Optional condition. If false, returns baseFilterOptions unchanged
 * @returns The modified filter options array
 */
export function insertSemanticViewTypeFilter(
	baseFilterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[],
	filterToInsert: FilterOption,
	condition: boolean = true
): (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[] {
	if (!condition) {
		return baseFilterOptions;
	}

	// Filter out the native type filter since semantic view types replace it
	const filteredOptions = baseFilterOptions.filter(
		(option) =>
			option === FILTER_OPTIONS_DIVIDER ||
			(typeof option === 'object' &&
				option?.type !== FilterOptionType.NATIVE_TYPE)
	);

	// Find the index of TITLE filter to insert SEMANTIC_VIEW_TYPE after it
	const titleIndex = filteredOptions.findIndex(
		(option) =>
			typeof option === 'object' && option?.type === FilterOptionType.TITLE
	);

	if (titleIndex !== -1) {
		// Insert after TITLE
		return [
			...filteredOptions.slice(0, titleIndex + 1),
			filterToInsert,
			...filteredOptions.slice(titleIndex + 1),
		];
	}

	// Fallback: insert at the beginning if TITLE not found
	return [filterToInsert, ...filteredOptions];
}
